import React, {
  useEffect, useRef, useState,
} from 'react';

import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';

import {
  Icon, IconContainer, List, ListItem, ListItemText,
} from './index.styles';
import { Data } from './types';
import { reorder } from './utils';

interface DraggableListProps {
  data: Data[],
  onOrderChange?: (newData: Data[]) => void,
  highlightId?: string | number
}

const DraggableList = ({ data, onOrderChange, highlightId }: DraggableListProps) => {
  const [items, setItems] = useState(data);
  const didOrderChange = useRef(false);

  useEffect(() => {
    if (didOrderChange.current) {
      if (onOrderChange) onOrderChange(items);
    }
    // Prevent the onOrderChange function from running on first render
    didOrderChange.current = true;
  }, [items]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    setItems(reorder(
      items,
      result.source.index,
      result.destination.index,
    ));
  };

  return (
    <List>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(droppableProvided) => (
            <div
              {...droppableProvided.droppableProps}
              ref={droppableProvided.innerRef}
            >
              {items.map((item, index) => (
                <Draggable
                  key={item.id}
                  draggableId={item.id?.toString() || ''}
                  index={index}
                >
                  {(draggableProvided) => (
                    <ListItem
                      index={index}
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      {...draggableProvided.dragHandleProps}
                    >
                      <IconContainer>
                        <Icon
                          highlight={(highlightId === item.id).toString()}
                        />
                      </IconContainer>
                      <ListItemText
                        variant="body2"
                        highlight={(highlightId === item.id).toString()}
                      >
                        {item.label}
                      </ListItemText>
                    </ListItem>
                  )}
                </Draggable>
              ))}
              {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </List>
  );
};

export default DraggableList;
